import React, { useState, useCallback, useEffect, useRef } from 'react';
import { searchEntreprise } from '../services/api';
import './ImmeubleSelector.css';

function ImmeubleSelector({ onSelectionComplete, currentSelection }) {
  // Étapes du parcours
  const [step, setStep] = useState(currentSelection ? 3 : 1);
  const [typeImmeuble, setTypeImmeuble] = useState(currentSelection?.typeImmeuble || null);
  const [typeProprietaire, setTypeProprietaire] = useState(currentSelection?.typeProprietaire || null);
  const [syndicData, setSyndicData] = useState(currentSelection?.syndicData || null);

  // Recherche syndic
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef(null);
  const resultsRef = useRef(null);

  // Fermer les résultats si clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recherche avec debounce
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      const results = await searchEntreprise(query);
      setSearchResults(results);
      setShowResults(true);
      setIsSearching(false);
    }, 400);
  }, []);

  // Étape 1 : Choix Monopropriété / Copropriété
  const handleTypeImmeubleSelect = (type) => {
    setTypeImmeuble(type);
    setTypeProprietaire(null);
    setSyndicData(null);
    setStep(2);
  };

  // Étape 2 : Choix du type de propriétaire/syndic
  const handleTypeProprietaireSelect = (type) => {
    setTypeProprietaire(type);

    // Si pas besoin de recherche API, finaliser directement
    if (type !== 'syndic_professionnel') {
      setSyndicData(null);
      finalizeSelection(typeImmeuble, type, null);
    } else {
      setStep(3); // Aller à la recherche syndic
    }
  };

  // Sélection d'une entreprise syndic
  const handleEntrepriseSelect = (entreprise) => {
    setSearchQuery(entreprise.nom);
    setShowResults(false);
    setSyndicData(entreprise);
    finalizeSelection(typeImmeuble, typeProprietaire, entreprise);
  };

  // Finaliser la sélection
  const finalizeSelection = (immType, propType, syndic) => {
    onSelectionComplete({
      typeImmeuble: immType,
      typeProprietaire: propType,
      syndicData: syndic
    });
    setStep(4); // Afficher le résumé
  };

  // Réinitialiser
  const handleReset = () => {
    setStep(1);
    setTypeImmeuble(null);
    setTypeProprietaire(null);
    setSyndicData(null);
    setSearchQuery('');
    setSearchResults([]);
    onSelectionComplete(null);
  };

  // Revenir en arrière
  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setTypeImmeuble(null);
    } else if (step === 3) {
      setStep(2);
      setTypeProprietaire(null);
      setSyndicData(null);
      setSearchQuery('');
    }
  };

  // Labels pour l'affichage
  const getTypeProprietaireLabel = (type) => {
    switch (type) {
      case 'pno_particulier': return 'Propriétaire non occupant - Particulier';
      case 'pno_entreprise': return 'Propriétaire non occupant - Entreprise';
      case 'syndic_benevole': return 'Syndic Bénévole';
      case 'syndic_professionnel': return 'Syndic Professionnel';
      default: return type;
    }
  };

  return (
    <div className="immeuble-selector">
      <div className="selector-header">
        <div className="selector-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21h18" />
            <path d="M5 21V7l8-4v18" />
            <path d="M19 21V11l-6-4" />
            <path d="M9 9v.01" />
            <path d="M9 12v.01" />
            <path d="M9 15v.01" />
            <path d="M9 18v.01" />
          </svg>
        </div>
        <div className="selector-title">
          <h3>Configuration de l'immeuble</h3>
          <p>Définissez le type de bien et de gestion</p>
        </div>
        {step === 4 && (
          <button className="selector-reset-btn" onClick={handleReset}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            Modifier
          </button>
        )}
      </div>

      {/* Indicateur de progression */}
      {step < 4 && (
        <div className="progress-indicator">
          <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Type</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Gestion</span>
          </div>
          {typeProprietaire === 'syndic_professionnel' && (
            <>
              <div className="progress-line"></div>
              <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
                <span className="step-number">3</span>
                <span className="step-label">Syndic</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Étape 1 : Type d'immeuble */}
      {step === 1 && (
        <div className="step-content">
          <h4 className="step-title">Quel est le type de propriété ?</h4>
          <div className="choice-buttons">
            <button
              className="choice-btn"
              onClick={() => handleTypeImmeubleSelect('monopropriete')}
            >
              <div className="choice-icon mono">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18" />
                  <path d="M9 21V12h6v9" />
                  <path d="M4 21V9l8-6 8 6v12" />
                </svg>
              </div>
              <div className="choice-content">
                <span className="choice-title">Monopropriété</span>
                <span className="choice-desc">Un seul propriétaire</span>
              </div>
            </button>

            <button
              className="choice-btn"
              onClick={() => handleTypeImmeubleSelect('copropriete')}
            >
              <div className="choice-icon copro">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18" />
                  <path d="M5 21V7l8-4v18" />
                  <path d="M19 21V11l-6-4" />
                  <path d="M9 9v.01" />
                  <path d="M9 12v.01" />
                  <path d="M9 15v.01" />
                </svg>
              </div>
              <div className="choice-content">
                <span className="choice-title">Copropriété</span>
                <span className="choice-desc">Plusieurs propriétaires</span>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Étape 2 : Type de propriétaire/syndic */}
      {step === 2 && (
        <div className="step-content">
          <button className="back-btn" onClick={handleBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Retour
          </button>

          {typeImmeuble === 'monopropriete' ? (
            <>
              <h4 className="step-title">Quel est le type de propriétaire ?</h4>
              <div className="choice-buttons">
                <button
                  className="choice-btn"
                  onClick={() => handleTypeProprietaireSelect('pno_particulier')}
                >
                  <div className="choice-icon particulier">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <div className="choice-content">
                    <span className="choice-title">Particulier</span>
                    <span className="choice-desc">Propriétaire non occupant - Particulier</span>
                  </div>
                </button>

                <button
                  className="choice-btn"
                  onClick={() => handleTypeProprietaireSelect('pno_entreprise')}
                >
                  <div className="choice-icon entreprise">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                  </div>
                  <div className="choice-content">
                    <span className="choice-title">Entreprise</span>
                    <span className="choice-desc">Propriétaire non occupant - Entreprise</span>
                  </div>
                </button>
              </div>
            </>
          ) : (
            <>
              <h4 className="step-title">Quel est le type de syndic ?</h4>
              <div className="choice-buttons">
                <button
                  className="choice-btn"
                  onClick={() => handleTypeProprietaireSelect('syndic_benevole')}
                >
                  <div className="choice-icon benevole">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <div className="choice-content">
                    <span className="choice-title">Syndic Bénévole</span>
                    <span className="choice-desc">Copropriétaire gérant bénévolement</span>
                  </div>
                </button>

                <button
                  className="choice-btn"
                  onClick={() => handleTypeProprietaireSelect('syndic_professionnel')}
                >
                  <div className="choice-icon professionnel">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                  </div>
                  <div className="choice-content">
                    <span className="choice-title">Syndic Professionnel</span>
                    <span className="choice-desc">Société de gestion immobilière</span>
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Étape 3 : Recherche syndic professionnel */}
      {step === 3 && typeProprietaire === 'syndic_professionnel' && (
        <div className="step-content">
          <button className="back-btn" onClick={handleBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Retour
          </button>

          <h4 className="step-title">Rechercher le syndic professionnel</h4>

          <div className="syndic-search-container" ref={resultsRef}>
            <div className="search-input-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                className="syndic-search-input"
                placeholder="Rechercher par nom ou SIRET..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => searchResults.length > 0 && setShowResults(true)}
              />
              {isSearching && <div className="search-spinner"></div>}
            </div>

            {showResults && searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((entreprise, index) => (
                  <button
                    key={index}
                    className="search-result-item"
                    onClick={() => handleEntrepriseSelect(entreprise)}
                  >
                    <div className="result-main">
                      <span className="result-name">{entreprise.nom}</span>
                      <span className="result-siret">SIRET: {entreprise.siret}</span>
                    </div>
                    <div className="result-address">
                      {entreprise.adresse && `${entreprise.adresse}, `}
                      {entreprise.codePostal} {entreprise.ville}
                    </div>
                    {entreprise.codeNaf && (
                      <div className="result-naf">NAF: {entreprise.codeNaf}</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
              <div className="search-no-results">
                <span>Aucune entreprise trouvée pour "{searchQuery}"</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Étape 4 : Résumé de la sélection */}
      {step === 4 && (
        <div className="selection-summary">
          <div className="summary-item">
            <div className={`summary-icon ${typeImmeuble}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
              </svg>
            </div>
            <div className="summary-content">
              <span className="summary-label">Type de bien</span>
              <span className="summary-value">
                {typeImmeuble === 'monopropriete' ? 'Monopropriété' : 'Copropriété'}
              </span>
            </div>
          </div>

          <div className="summary-divider"></div>

          <div className="summary-item">
            <div className={`summary-icon ${typeProprietaire}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {typeProprietaire?.includes('particulier') || typeProprietaire?.includes('benevole') ? (
                  <>
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </>
                ) : (
                  <>
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  </>
                )}
              </svg>
            </div>
            <div className="summary-content">
              <span className="summary-label">Mode de gestion</span>
              <span className="summary-value">{getTypeProprietaireLabel(typeProprietaire)}</span>
            </div>
          </div>

          {syndicData && (
            <>
              <div className="summary-divider"></div>
              <div className="summary-item syndic-details">
                <div className="summary-icon professionnel">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  </svg>
                </div>
                <div className="summary-content">
                  <span className="summary-label">Syndic</span>
                  <span className="summary-value">{syndicData.nom}</span>
                  <div className="syndic-info">
                    <span>SIRET: {syndicData.siret}</span>
                    <span>{syndicData.adresse}, {syndicData.codePostal} {syndicData.ville}</span>
                    {syndicData.codeNaf && <span>NAF: {syndicData.codeNaf}</span>}
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="summary-check">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImmeubleSelector;
